import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Participant, InviteStatus } from '../../types/generated/graphql';
import { colors } from '../../theme/colors';
import GravatarAvatar from '../GravatarAvatar';

export type AssigneeControlMode = 'role' | 'step' | 'delegate';

interface AssigneeControlProps {
  /** The currently assigned participant */
  assignee?: Participant | null;
  /** Whether this is the current user */
  isCurrentUser?: boolean;
  /** Label shown when no assignee (e.g., "Assign role", "Delegate task") */
  emptyLabel?: string;
  /** Mode for styling and behavior */
  mode?: AssigneeControlMode;
  /** Callback when clicking assign/change button */
  onAssign?: () => void;
  /** Show compact version (inline) */
  compact?: boolean;
  /** Disable interactions */
  disabled?: boolean;
}

/**
 * Reusable assignee control - shows assigned person pill with Change button.
 * Used for both role assignment and step delegation.
 *
 * States:
 * - Empty: Shows "Assign" button with person add icon
 * - Assigned: Shows avatar pill with name and "Change" button
 */
export const AssigneeControl: React.FC<AssigneeControlProps> = ({
  assignee,
  isCurrentUser = false,
  emptyLabel = 'Assign someone',
  mode = 'role',
  onAssign,
  compact = false,
  disabled = false,
}) => {
  const getInviteStatusChip = () => {
    if (!assignee?.inviteStatus || assignee.inviteStatus === InviteStatus.Accepted) {
      return null;
    }

    const statusConfig = {
      [InviteStatus.Sent]: { label: 'Invited', color: colors.blue[600], bg: colors.blue[50] },
      [InviteStatus.Expired]: { label: 'Expired', color: colors.red[600], bg: colors.red[50] },
      [InviteStatus.NotSent]: { label: 'Not invited', color: colors.gray[500], bg: colors.gray[100] },
    };

    const config = statusConfig[assignee.inviteStatus];
    if (!config) return null;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          height: 18,
          bgcolor: config.bg,
          color: config.color,
          fontSize: '0.6rem',
          fontWeight: 600,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  };

  const getModeAccentColor = () => {
    switch (mode) {
      case 'delegate':
        return colors.amber[500];
      case 'step':
        return colors.blue[500];
      case 'role':
      default:
        return colors.blue[600];
    }
  };

  // Empty state - no assignee
  if (!assignee) {
    return (
      <Button
        size="small"
        startIcon={<PersonAddOutlinedIcon sx={{ fontSize: 16 }} />}
        onClick={onAssign}
        disabled={disabled}
        sx={{
          textTransform: 'none',
          color: getModeAccentColor(),
          fontSize: compact ? '0.75rem' : '0.8rem',
          px: compact ? 1 : 1.5,
          py: compact ? 0.25 : 0.5,
          borderRadius: 1,
          '&:hover': {
            bgcolor: mode === 'delegate' ? colors.amber[50] : colors.blue[50],
          },
        }}
      >
        {emptyLabel}
      </Button>
    );
  }

  // Assigned state - show pill with avatar
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 1 : 1.5,
        p: compact ? 0.5 : 1,
        pl: compact ? 0.5 : 1.5,
        bgcolor: colors.gray[50],
        borderRadius: 2,
        border: `1px solid ${colors.gray[200]}`,
        transition: 'border-color 0.15s',
        '&:hover': {
          borderColor: getModeAccentColor(),
        },
      }}
    >
      {/* Avatar */}
      <GravatarAvatar
        email={assignee.email}
        name={assignee.name}
        size={compact ? 24 : 32}
        showTooltip={false}
      />

      {/* Name and status */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant={compact ? 'caption' : 'body2'}
            sx={{
              fontWeight: 500,
              color: colors.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isCurrentUser ? 'You' : assignee.name}
          </Typography>
          {isCurrentUser && (
            <Chip
              label="You"
              size="small"
              sx={{
                height: 16,
                bgcolor: colors.blue[50],
                color: colors.blue[600],
                fontSize: '0.55rem',
                fontWeight: 600,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
          {!isCurrentUser && getInviteStatusChip()}
        </Box>
        {!compact && assignee.email && (
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {assignee.email}
          </Typography>
        )}
      </Box>

      {/* Change button */}
      <Button
        size="small"
        startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={onAssign}
        disabled={disabled}
        sx={{
          textTransform: 'none',
          color: colors.gray[600],
          fontSize: '0.75rem',
          px: 1,
          py: 0.25,
          minWidth: 'auto',
          '&:hover': {
            bgcolor: colors.gray[100],
            color: getModeAccentColor(),
          },
        }}
      >
        Change
      </Button>
    </Box>
  );
};

export default AssigneeControl;
